  // Create folder in Firestore if not exists (case-insensitive)

  // Suggest folders based on first name (case-insensitive, substring match)
import React, { useState, useEffect } from "react";
import { Folder as LucideFolder, File as LucideFile, Download, Trash2, Users, Upload, Link2, File as FileIcon, FileText, FileImage } from "lucide-react";
import { getFirestore, collection, query, where, getDocs, setDoc, doc, updateDoc, arrayUnion, serverTimestamp, addDoc, Timestamp } from "firebase/firestore";
import { app } from "../firebase";
import FileUploadArea from "./FileUploadArea"; // You should have a file upload component

const db = getFirestore(app);

const PublicFolderUpload = () => {
  // ...existing state and hooks...

  // Suggest folders based on first name (case-insensitive, substring match)
  const handleFirstNameBlur = async () => {
    const trimmedFirst = firstName.trim().toLowerCase();
    if (trimmedFirst) {
      setLoading(true);
      try {
        const q2 = query(collection(db, "folderStructures"));
        const snap2 = await getDocs(q2);
        const allFolders = snap2.docs.map(doc => ({ id: doc.id, name: doc.data().name }));
        const match = allFolders.find(f => f.name.trim().toLowerCase().includes(trimmedFirst));
        // Suggest all folders that contain the first name (not just the first match)
        const suggestions = allFolders
          .filter(f => f.name.trim().toLowerCase().includes(trimmedFirst))
          .map(f => f.name);
        setSuggestedFolders(suggestions);
        setShowSuggestions(suggestions.length > 0);
        if (match) {
          setFolderExists(true);
          setReadonly(true);
          setSuccess("Folder already exists. You can upload files to it.");
          setFolderId(match.id);
        } else {
          setFolderExists(false);
          setReadonly(false);
          setSuccess("");
          setFolderId(null);
        }
      } catch (err) {
        setError("Error checking folder: " + err.message);
      }
      setLoading(false);
    }
  };
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [folderName, setFolderName] = useState("");
  const [folderId, setFolderId] = useState(null);
  const [folderExists, setFolderExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [readonly, setReadonly] = useState(false);
  const [suggestedFolders, setSuggestedFolders] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

    const createFolder = async () => {
    const trimmedName = folderName.trim();
    if (!trimmedName) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      // Check for existing folder
      const q2 = query(collection(db, "folderStructures"));
      const snap2 = await getDocs(q2);
      const match = snap2.docs.find(doc => (doc.data().name || "").trim().toLowerCase() === trimmedName.toLowerCase());
      if (match) {
        setFolderExists(true);
        setSuccess("Folder already exists. You can upload files to it.");
        setFolderName(trimmedName);
        setFolderId(match.id);
        setReadonly(true);
      } else {
        // Create new folder
        const now = Timestamp.now();
        const docRef = await addDoc(collection(db, "folderStructures"), {
          name: trimmedName,
          parentId: null,
          createdAt: now,
          updatedAt: now,
          modifiedAt: now,
          files: [],
        });
        setFolderExists(true);
        setReadonly(true);
        setSuccess("Folder created. You can now upload files.");
        setFolderName(trimmedName);
        setFolderId(docRef.id);
      }
    } catch (err) {
      setError("Error creating folder: " + err.message);
    }
    setLoading(false);
  };
  
  // Check if folder exists in either collection (trimmed, case-insensitive, substring match)
  const checkFolderExists = async (name) => {
    setLoading(true);
    setError("");
    setSuccess("");
    const trimmedName = name.trim();
    try {
      const trimmedNameLower = trimmedName.toLowerCase();
      const q2 = query(collection(db, "folderStructures"));
      const snap2 = await getDocs(q2);
      const allFolders = snap2.docs.map(doc => ({ id: doc.id, name: doc.data().name }));
      const match = allFolders.find(f => f.name.trim().toLowerCase() === trimmedNameLower);
      if (match) {
        setFolderExists(true);
        setSuccess("Folder already exists. You can upload files to it.");
        setFolderName(trimmedName); // ensure trimmed
        setFolderId(match.id);
      } else {
        setFolderExists(false);
        setReadonly(false);
        setSuccess("");
        setFolderId(null);
      }
    } catch (err) {
      setError("Error creating folder: " + err.message);
    }
    setLoading(false);
  };


  // Debounced folder existence check and suggestions (5s for suggestions)
  useEffect(() => {
    let checkHandler;
    let suggestHandler;
    const combinedName = firstName.trim() && lastName.trim() ? `${firstName.trim()} ${lastName.trim()}` : "";
    setFolderName(combinedName);
    if (combinedName) {
      checkHandler = setTimeout(() => {
        checkFolderExists(combinedName);
      }, 400);
      suggestHandler = setTimeout(async () => {
        // Suggest folders containing the search word (case-insensitive, substring match)
        const q = query(collection(db, "folderStructures"));
        const querySnapshot = await getDocs(q);
        const searchLower = combinedName.trim().toLowerCase();
        const suggestions = querySnapshot.docs
          .map(doc => doc.data().name)
          .filter(name => name.toLowerCase().includes(searchLower) && name.toLowerCase() !== searchLower);
        setSuggestedFolders(suggestions);
        setShowSuggestions(suggestions.length > 0);
      }, 1000);
    } else {
      setFolderExists(false);
      setReadonly(false);
      setSuccess("");
      setSuggestedFolders([]);
      setShowSuggestions(false);
    }
    return () => {
      clearTimeout(checkHandler);
      clearTimeout(suggestHandler);
    };
    // eslint-disable-next-line
  }, [firstName, lastName]);

  // Public upload link (icon only UI)
  const publicLink = `${window.location.origin}/origin/public-folder-upload`;
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(publicLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Helper for file type icon in suggestions
  const getFileTypeIcon = (name) => {
    const ext = name.split('.').pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext)) return <FileImage size={16} className="text-blue-400" />;
    if (["pdf"].includes(ext)) return <FileIcon size={16} className="text-red-500" />;
    if (["doc", "docx", "odt"].includes(ext)) return <FileIcon size={16} className="text-blue-700" />;
    if (["txt", "md"].includes(ext)) return <FileText size={16} className="text-gray-500" />;
    return <LucideFolder size={16} />;
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#0071bd] to-[#6dd5ed] p-4">
      {/* Public upload link with icons only */}
      {/* <div className="flex gap-4 items-center mb-6 bg-white/80 rounded-xl px-4 py-2 shadow">
        <span title="Public Upload" className="text-[#0071bd]"> <Users size={22} /> </span>
        <span title="Upload" className="text-green-600"> <Upload size={22} /> </span>
        <button
          onClick={handleCopy}
          className={`rounded-full p-2 ${copied ? 'bg-green-100' : 'hover:bg-gray-200'} transition`}
          title={copied ? 'Copied!' : 'Copy Link'}
        >
          <Link2 size={22} className={copied ? 'text-green-600' : 'text-gray-700'} />
        </button>
      </div> */}
      <div className="max-w-lg w-full bg-white/90 rounded-2xl shadow-2xl p-8 animate-fade-in-up">
        <h2 className="text-2xl font-bold text-center text-[#0071bd] mb-6">Create or Access Your Folder</h2>
        {/* Note Section */}
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded text-yellow-900 text-sm flex flex-col gap-1">
          <span className="font-semibold flex items-center gap-1"><span className="text-lg">&#9888;</span> Please upload the following documents for Job Process:</span>
          <ul className="list-decimal list-inside ml-2">
            <li>Upload <span className="font-semibold text-[#0071bd]">Aadhar Card</span></li>
            <li>Upload <span className="font-semibold text-[#0071bd]">Pan Card</span></li>
            <li>Upload <span className="font-semibold text-[#0071bd]">Passport size photo</span></li>
            <li>Upload <span className="font-semibold text-[#0071bd]">Bank Passbook copy</span></li>
            <li>Upload <span className="font-semibold text-[#0071bd]">Resume</span></li>
           <li>Upload <span className="font-semibold text-[#0071bd]">EPFO Service History if having exp</span></li>
            <li>
              Please edit the sample 
              <a
                href={process.env.PUBLIC_URL ? process.env.PUBLIC_URL + '/assets/Template-PersonalDetails.docx' : '/assets/Template-PersonalDetails.docx'}
                download
                className="font-semibold text-blue-700 underline hover:text-blue-900 mx-1"
                style={{ textDecoration: "underline", fontWeight: "bold", color:"blue" }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Template-PersonalDetails.docx
              </a>
              then re-upload it here.
            </li>
          </ul>
        </div>
        <div className="mb-4 flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0071bd] transition duration-150 ease-in-out"
              placeholder="First Name"
              value={firstName}
              onChange={e => { setFirstName(e.target.value.replace(/[^a-zA-Z ]/g, "")); setReadonly(false); setSuccess(""); setError(""); }}
              onBlur={handleFirstNameBlur}
          
              disabled={loading}
            />
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0071bd] transition duration-150 ease-in-out"
              placeholder="Last Name"
              value={lastName}
              onChange={e => { setLastName(e.target.value.replace(/[^a-zA-Z ]/g, "")); setReadonly(false); setSuccess(""); setError(""); }}
             
              disabled={loading}
            />
            {!readonly && (
              <button
                className={`px-4 py-2 rounded-md transition ${loading || !folderName || folderExists || (suggestedFolders.length > 0) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#0071bd] text-white hover:bg-[#005fa3]'}`}
                onClick={createFolder}
                disabled={loading || !folderName || folderExists || (suggestedFolders.length > 0)}
              >
                Create
              </button>
            )}
          </div>
          {showSuggestions && suggestedFolders.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mt-1">
              <div className="text-xs text-blue-700 mb-1">Similar folders found. You can upload files to these:</div>
              <ul className="flex flex-wrap gap-2">
                {suggestedFolders.map(name => (
                  <li key={name}>
                    <button
                      className="flex items-center gap-1 px-3 py-1 bg-white border border-blue-300 rounded hover:bg-blue-100 transition text-blue-700 text-sm"
                      onClick={async () => { 
                        const [fn, ln] = name.split('_');
                        setFirstName(fn || "");
                        setLastName(ln || "");
                        setFolderName(name);
                        setReadonly(true); 
                        setShowSuggestions(false); 
                        // Find the folderId for the selected suggestion
                        try {
                          const q2 = query(collection(db, "folderStructures"));
                          const snap2 = await getDocs(q2);
                          const match = snap2.docs.find(doc => (doc.data().name || "").trim().toLowerCase() === name.trim().toLowerCase());
                          if (match) {
                            setFolderId(match.id);
                            setFolderExists(true);
                            setSuccess("Folder already exists. You can upload files to it.");
                          } else {
                            setFolderId(null);
                            setFolderExists(false);
                            setSuccess("");
                          }
                        } catch (err) {
                          setError("Error finding folder: " + err.message);
                        }
                      }}
                    >
                      {getFileTypeIcon(name)}
                      {name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {success && <div className="mb-2 p-2 bg-green-100 text-green-700 rounded">{success}</div>}
        {error && <div className="mb-2 p-2 bg-red-100 text-red-700 rounded">{error}</div>}
        {folderExists && folderId && (
          <FileUploadArea 
            folderId={folderId}
            folderName={folderName.trim()} 
            readonlyFolderName={false} 
          />
        )}
      </div>
    </div>
  );
};

export default PublicFolderUpload;
